<template>
  <div class="tw-relative tw-inline-block tw-text-left">
    <div class="tw-flex tw-items-center tw-gap-1.5">
      <button
        @click="isOpen = !isOpen"
        class="tw-relative tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-backdrop-blur tw-border tw-rounded-xl tw-shadow-sm tw-transition-all tw-text-sm tw-font-medium"
        :class="isActive 
          ? 'tw-bg-slate-900 tw-border-slate-900 tw-text-white hover:tw-bg-slate-800' 
          : ('tw-border-slate-200 tw-text-slate-400 tw-opacity-80 ' + (isOpen ? ' tw-bg-slate-400 tw-text-white' : ' tw-bg-white/60 hover:tw-text-slate-600 hover:tw-bg-white'))"
      >
        <font-awesome-icon
          icon="filter"
          :class="isActive ? 'tw-text-white/80' : (isOpen ? ' tw-text-white' : 'tw-text-slate-400')"
        />
        advanced sort
    
        <span v-if="!isActive && modelValue.length > 1" class="tw-absolute tw-top-[-1px] tw-right-[-1px] tw-flex tw-h-2.5 tw-w-2.5">
          <span class="tw-animate-ping tw-absolute tw-inline-flex tw-h-full tw-w-full tw-rounded-full tw-bg-slate-400 tw-opacity-75"></span>
          <span class="tw-relative tw-inline-flex tw-rounded-full tw-h-2.5 tw-w-2.5 tw-bg-slate-900"></span>
        </span>

        <font-awesome-icon
          v-if="isOpen"
          icon="circle-xmark"
          class="tw-absolute tw-text-black tw-top-[-5px] tw-right-[-5px] tw-flex tw-h-2.5 tw-w-2.5"
        />
      </button>

      <button
        v-if="!isActive && modelValue.length > 1"
        @click="$emit('activate')"
        class="tw-p-2 tw-text-slate-600 tw-bg-white/60 tw-border tw-border-slate-200 tw-rounded-xl tw-shadow-sm hover:tw-bg-white hover:tw-text-slate-900 tw-transition-all"
        title="Restore advanced sort"
      >
        <font-awesome-icon icon="rotate-left" class="tw-text-xs" />
      </button>
    </div>

    <div
      v-if="isOpen"
      class="tw-absolute tw-left-0 tw-mt-2 tw-z-50 tw-w-80 tw-bg-white tw-border tw-border-slate-200 tw-rounded-2xl tw-shadow-xl tw-p-4"
    >
      <div class="tw-flex tw-flex-col tw-gap-3">

        <div v-for="(level, index) in sortLevels" :key="level.id" class="tw-flex tw-items-center tw-gap-2">
          <span class="tw-text-xs tw-text-slate-400 tw-w-12 tw-shrink-0">
            {{ index === 0 ? 'sort by' : 'then by' }}
          </span>

          <select
            v-model="level.field"
            class="tw-flex-1 tw-text-xs tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-lg tw-px-2 tw-py-1.5 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-indigo-500"
          >
            <option 
              v-for="field in availableFields" 
              :key="field.value" 
              :value="field.value"
              :disabled="isFieldDisabled(field.value, level.id)"
            >
              {{ field.label }}
            </option>
          </select>

          <button
            @click="toggleOrder(index)"
            class="tw-p-1.5 tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded-lg hover:tw-bg-slate-100"
            title="Toggle Order"
          >
            <font-awesome-icon
              :icon="level.order === 'ASC' ? 'sort-amount-up' : 'sort-amount-down'"
              class="tw-text-slate-600 tw-w-3 tw-h-3"
            />
          </button>

          <button
            v-if="sortLevels.length > 1"
            @click="removeLevel(index)"
            class="tw-text-slate-300 hover:tw-text-red-500 tw-transition-colors"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>

        <button
          v-if="sortLevels.length < availableFields.length"
          @click="addLevel"
          class="tw-text-left tw-text-xs tw-text-indigo-600 hover:tw-text-indigo-800 tw-font-medium tw-mt-1 tw-inline-flex tw-items-center tw-gap-1"
        >
          <font-awesome-icon icon="plus" class="tw-text-[10px]" />
          add sort level
        </button>

        <hr class="tw-border-slate-100 tw-my-1" />

        <div class="tw-flex tw-justify-end tw-gap-2">
          <button @click="resetSort" class="tw-text-xs tw-text-slate-500 hover:tw-text-slate-700">Reset</button>
          <button
            @click="applySort"
            class="tw-text-xs tw-bg-slate-900 tw-text-white tw-px-3 tw-py-1.5 tw-rounded-lg hover:tw-bg-slate-800"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

const genId = () => Math.random().toString(36).substr(2, 9);

export default defineComponent({
  name: 'AdvancedSort',
  props: {
    availableFields: {
      type: Array as () => Array<{ label: string, value: string }>,
      required: true,
    },
    modelValue: {
      type: Array as () => Array<{ field: string, order: 'ASC' | 'DESC' }>,
      default: () => [],
    },
    isActive: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: false,
      sortLevels: [] as Array<{ id: string, field: string, order: 'ASC' | 'DESC' }>,
    };
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.syncFromProps();
      }
    },
    modelValue: {
      immediate: true,
      handler(val) {
        if (val && val.length > 0) {
          this.sortLevels = val.map((l: any) => ({
            id: l.id || genId(),
            field: l.field,
            order: l.order,
          }));
        } else {
          this.sortLevels = [{ id: genId(), field: this.availableFields[0].value, order: 'ASC' }];
        }
      },
    },
  },
  methods: {
    syncFromProps() {
      if (this.modelValue && this.modelValue.length > 0) {
        this.sortLevels = this.modelValue.map((l: any) => ({ 
          id: genId(), field: l.field, order: l.order 
        }));
      } else {
        this.sortLevels = [{ id: genId(), field: this.availableFields[0].value, order: 'ASC' }];
      }
    },
    addLevel() {
      const usedFields = this.sortLevels.map(l => l.field);
      const nextField = this.availableFields.find(f => !usedFields.includes(f.value)) || this.availableFields[0];
      this.sortLevels.push({ id: genId(), field: nextField.value, order: 'ASC' });
    },
    removeLevel(index: number) {
      this.sortLevels.splice(index, 1);
    },
    toggleOrder(index: number) {
      this.sortLevels[index].order = this.sortLevels[index].order === 'ASC' ? 'DESC' : 'ASC';
    },
    resetSort() {
      this.sortLevels = [{ id: genId(), field: this.availableFields[0].value, order: 'ASC' }];
    },
    applySort() {
      const output = this.sortLevels.map(l => ({ field: l.field, order: l.order }));
      this.$emit('update:modelValue', output);
      this.isOpen = false;
    },
    emitUpdate() {
      const output = this.sortLevels.map(l => ({ field: l.field, order: l.order }));
      this.$emit('update:modelValue', output);
    },
    getAvailableFieldsForLevel(currentIndex: number) {
      const selectedFields = this.sortLevels
        .filter((_, idx) => idx !== currentIndex)
        .map(l => l.field);
      return this.availableFields.filter(f => !selectedFields.includes(f.value));
    },
    isFieldDisabled(fieldValue: string, currentLevelId: string) {
      return this.sortLevels.some(l => l.field === fieldValue && l.id !== currentLevelId);
    },
  },
});
</script>